<template>
    <BaseModal @close="$emit('close')" style="z-index: 5;">
        <div class="avatar_cropper_container">
            <div class="avatar_cropper">
                <button type="button" class="avatar_cropper_close" @click="$emit('close')">
                    <img :src="currentImage" data-dark="/close.svg" data-light="/close-light.svg">
                </button>

                <div class="cropper_wrapper" ref="cropperWrapper">
                    <VuePictureCropper :boxStyle="{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f8f8f8',
                        margin: 'auto',
                    }" :img="image" :options="cropperOptions" />
                </div>

                <button type="button" class="avatar_cropper_save" @click="getResult">
                    {{ $t('profileEdit.save') }}
                </button>
            </div>
        </div>
    </BaseModal>
</template>

<script>
import { defineComponent, ref } from 'vue'
import VuePictureCropper, { cropper } from 'vue-picture-cropper'
import BaseModal from "@/UI/Modals/BaseModal.vue";
import { useThemeStore } from '@/stores/themeStore';

export default defineComponent({
    components: {
        VuePictureCropper, BaseModal
    },
    setup() {
        const cropperWrapper = ref(null)

        return {
            cropperWrapper
        }
    },
    data() {
        return {
            result: {
                dataURL: '',
                blobURL: '',
            },
            themeStore: useThemeStore(),
            currentImage: "",
            cropperOptions: {
                viewMode: 1, // Ограничивает кропер границами контейнера
                dragMode: 'move',
                background: false,
                responsive: true,
                restore: true,
                autoCropArea: 1,
                aspectRatio: 1,
                minCropBoxWidth: 50,
                minCropBoxHeight: 50,
                checkCrossOrigin: false,
                guides: true,
                center: true,
                highlight: true,
                movable: true,
                rotatable: true,
                scalable: true,
                zoomable: true,
                zoomOnTouch: true,
                zoomOnWheel: true,
                wheelZoomRatio: 0.1,
                cropBoxMovable: true,
                cropBoxResizable: true,
                toggleDragModeOnDblclick: true,
            },
            resizeObserver: null
        }
    },
    props: {
        image: {
            type: String,
            required: true,
        },
    },
    methods: {
        updateImage() {
            this.currentImage = this.themeStore.isDark
                ? new URL('/close.svg', import.meta.url).href
                : new URL('/close-light.svg', import.meta.url).href;
        },
        async getResult() {
            if (!cropper) return

            try {
                const base64 = cropper.getDataURL()
                const blob = await cropper.getBlob()

                if (!blob) return

                const file = await cropper.getFile({
                    fileName: "cropped_avatar.png",
                })

                this.result.dataURL = base64
                this.result.blobURL = URL.createObjectURL(blob)

                this.$emit('cropped', { base64, blob, file })
            } catch (error) {
                console.error('Error getting cropped result:', error)
            }
        },
        clear() {
            if (!cropper) return
            cropper.clear()
        },
        reset() {
            if (!cropper) return
            cropper.reset()
        },
    },
    watch: {
        "themeStore.isDark": function () {
            this.updateImage();
        },
    },
    mounted() {
        this.updateImage();
    },
    beforeUnmount() {
        if (this.result.blobURL) {
            URL.revokeObjectURL(this.result.blobURL)
        }
    }
})
</script>

<style scoped>
.avatar_cropper_container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 20px;
}

.cropper_wrapper,
.vue--picture-cropper__wrap {
    background-color: transparent !important;
}

.avatar_cropper {
    max-width: min(90svw, 800px);
    max-height: min(80svh, 600px);
    width: 100%;
    height: 100%;
    border-radius: 15px;
    background-color: var(--header-color);
    padding: 15px;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 3;
    overflow: hidden;
}

.cropper_wrapper {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 300px;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    background-color: #f8f8f8;
}

:deep(.vue-picture-cropper) {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
}

:deep(.cropper-container) {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
}

:deep(.cropper-crop-box),
:deep(.cropper-view-box) {
    outline: 1px solid var(--main-color) !important;
    outline-color: var(--main-color) !important;
    box-sizing: border-box !important;
}

:deep(.cropper-point) {
    background-color: var(--main-color) !important;
    width: 8px !important;
    height: 8px !important;
}

:deep(.cropper-line) {
    background-color: var(--main-color) !important;
    opacity: 0.5 !important;
}

.avatar_cropper_close {
    margin-bottom: 5px;
    border-radius: 50%;
    cursor: pointer;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    z-index: 10;
    padding: 8px;
    align-self: flex-end;
}

.avatar_cropper_close:hover {
    background: var(--hover);
}

.avatar_cropper_close img {
    width: 12px;
    height: 12px;
}

.avatar_cropper_save {
    align-self: flex-end;
    padding: 10px 30px;
    border-radius: 10px;
    margin-top: 20px;
    background: var(--edit-profile-submit);
    color: black;
    font-weight: bold;
    cursor: pointer;
    border: none;
    font-size: 16px;
    transition: background-color 0.3s ease;
}

.avatar_cropper_save:hover {
    background: var(--edit-profile-submit-hover);
}

@media (max-width: 768px) {
    .avatar_cropper {
        max-width: 95svw;
        padding: 15px;
    }

    .cropper_wrapper {
        min-height: 250px;
    }
}

@media (max-width: 480px) {
    .avatar_cropper {
        padding: 10px;
    }

    .cropper_wrapper {
        min-height: 200px;
    }

    .avatar_cropper_save {
        padding: 8px 20px;
        font-size: 14px;
    }
}
</style>
